import {
  convertLocalDateToOrgTimezone,
  convertOrgTimezoneToLocalDate,
} from '../../../utils/dateRangeTimezone';
import {
  LIST_OPERATORS,
  type ContactFilterCondition,
  type ContactFilterOperator,
} from '../../../types/contactFilter.types';

/**
 * Operators whose bound is the *end* of the picked day.
 *
 * The server compares the instant directly, so the boundary has to be exact:
 * "on or before Mar 1" is `<= Mar 1 23:59:59`, and "after Mar 1" is
 * `> Mar 1 23:59:59`. Sending start-of-day for these would exclude the whole
 * of Mar 1 and include the whole of it respectively.
 */
const END_OF_DAY_OPERATORS: ReadonlySet<ContactFilterOperator> = new Set<ContactFilterOperator>([
  'GT',
  'LTE',
]);

/**
 * The picked day as an instant, in the organisation's timezone.
 *
 * Org timezone rather than the browser's, so a date filter means the same
 * thing to everyone on the team — the same basis every other date filter in
 * the app uses via this helper.
 */
export const dayBoundISO = (
  date: Date,
  operator: ContactFilterOperator,
  timezone: string
): string =>
  convertLocalDateToOrgTimezone(date, timezone, END_OF_DAY_OPERATORS.has(operator)).toISOString();

/** Stored instant back to the calendar day it represents in the org timezone. */
export const parseStoredDate = (iso: string | null | undefined, timezone: string): Date | null => {
  if (!iso) return null;
  const parsed = new Date(iso);
  if (Number.isNaN(parsed.getTime())) return null;
  return convertOrgTimezoneToLocalDate(parsed, timezone);
};

/** Stored instant as its org-timezone calendar day, for the summary sentence. */
export const isoToDateLabel = (iso: string | null | undefined, timezone: string): string => {
  const parsed = parseStoredDate(iso, timezone);
  if (!parsed) return '';
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${parsed.getFullYear()}-${month}-${day}`;
};

/**
 * The condition patch for switching operator.
 *
 * Value and values are not interchangeable, so the one going unused is
 * cleared. A date keeps its calendar day, but the instant stored for it
 * depends on the operator: switching "on or after" to "on or before" has to
 * move the bound from the start of the day to its end, or the filter would
 * silently drop the rest of that day until the date was picked again.
 */
export const operatorChangePatch = (
  condition: ContactFilterCondition,
  operator: ContactFilterOperator,
  isDate: boolean,
  timezone: string
): Partial<ContactFilterCondition> => {
  if (LIST_OPERATORS.has(condition.operator) !== LIST_OPERATORS.has(operator)) {
    return { operator, value: null, values: null };
  }
  const day = isDate ? parseStoredDate(condition.value, timezone) : null;
  return day ? { operator, value: dayBoundISO(day, operator, timezone) } : { operator };
};
